import React from 'react'

import { Invoice, invoiceTypeChecker, ScopeType } from '@guiker/payment-shared'
import { AdminPropRentalContextProvider, PropRentalContextProvider, usePropRental } from '@guiker/prop-rental-context'
import { PropRental } from '@guiker/prop-rental-shared'
import { Flex, Link, P, PSmall, PSmaller, useTranslation } from '@guiker/react-framework'
import { currency, money, optionalConcat } from '@guiker/shared-framework'

import { getInvoiceReason } from '../../utils/get-invoice-reason'

type InvoiceReasonProps = {
  invoice: Invoice
  size?: 'medium' | 'small'
}

type WrapperProps = React.PropsWithChildren & {
  propRental?: PropRental
}

const PropRentalWrapper: React.FC<WrapperProps> = ({ propRental, children }) => {
  return !!propRental ? (
    <Link to={`/prop-rental/${propRental.id}/overview`} underline prefixWithLocale={false}>
      {children}
    </Link>
  ) : (
    <>{children}</>
  )
}

const InvoiceReasonContent: React.FC<InvoiceReasonProps> = ({ invoice, size = 'medium' }) => {
  const { t } = useTranslation(['main-propsharing', 'main-payment'])
  const tStockOrderPrefix = 'main-propsharing:screens.stockOrderCheckout.sections.orderDetails'
  const MainComponent = size === 'medium' ? P : PSmall
  const SecondaryComponent = size === 'medium' ? PSmall : PSmaller
  const reason = getInvoiceReason({ invoice })
  const { data: propRental } = usePropRental({ shouldThrowIfUndefined: false })

  if (invoiceTypeChecker.scope.isTenantInstalment(invoice) || invoiceTypeChecker.scope.isInvoiceDisputed(invoice)) {
    return (
      <Flex flexDirection='column'>
        <MainComponent>{reason}</MainComponent>
        <PropRentalWrapper propRental={propRental}>
          <SecondaryComponent color={60}>
            {invoice.info?.description ?? invoice.metadata.description}
          </SecondaryComponent>
        </PropRentalWrapper>
      </Flex>
    )
  } else if (invoiceTypeChecker.scope.isBuyStockOrder(invoice)) {
    const item = invoice.items[0]

    return (
      <Flex flexDirection='column'>
        <MainComponent component='span'>
          {reason}
          <MainComponent ml={0.5} component='span' color={60}>{`(x ${item.quantity})`}</MainComponent>
        </MainComponent>
        <SecondaryComponent color={60}>
          {t(`${tStockOrderPrefix}.pricePerUnit`, {
            price: money.fromAmount(item.pricePerUnit, currency[invoice.currency]).toString(),
          })}
        </SecondaryComponent>
      </Flex>
    )
  } else if (invoiceTypeChecker.scope.isFundingAccountDeposit(invoice)) {
    return (
      <Flex>
        <MainComponent>{reason}</MainComponent>
      </Flex>
    )
  } else if (invoiceTypeChecker.scope.isNone(invoice)) {
    const { listingId, unit } = { ...invoice.metadata }
    return (
      <Flex flexDirection='column'>
        <MainComponent>{reason}</MainComponent>
        {listingId || unit ? (
          <SecondaryComponent color={60}>{optionalConcat([listingId, unit], ' / ')}</SecondaryComponent>
        ) : (
          <></>
        )}
      </Flex>
    )
  } else {
    ;<div></div>
  }
}

const InvoiceReason: React.FC<InvoiceReasonProps> = ({ invoice, size = 'medium' }) => {
  return (
    <AdminPropRentalContextProvider>
      <PropRentalContextProvider listingId={(invoice as Invoice<ScopeType.TENANT_INSTALMENT>).metadata?.listingId}>
        <InvoiceReasonContent invoice={invoice} size={size} />
      </PropRentalContextProvider>
    </AdminPropRentalContextProvider>
  )
}

export { InvoiceReason }
